import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { FilterConfig } from '../../hooks/useIdeas';

interface IdeaFiltersProps {
  filterConfig: FilterConfig;
  showFilters: boolean;
  availablePlatforms: string[];
  availableSectors: string[];
  availableImpactLevels: string[];
  onFilterChange: (key: keyof FilterConfig, value: any) => void;
  onToggleFilters: () => void;
  onClearFilters: () => void;
}

const IdeaFilters: React.FC<IdeaFiltersProps> = ({
  filterConfig,
  showFilters,
  availablePlatforms,
  availableSectors,
  availableImpactLevels,
  onFilterChange,
  onToggleFilters,
  onClearFilters
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const activeFilterCount = Object.values(filterConfig).filter(v => v).length;

  return (
    <>
      {/* Filter Controls Section */}
      <Box component="section" aria-label="Ideas Controls" sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2, 
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h6" component="h2">
          All Ideas
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />} 
          onClick={onToggleFilters}
          aria-expanded={showFilters}
          aria-controls="filters-panel"
          fullWidth={isMobile}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'} ({activeFilterCount})
        </Button>
      </Box>

      <Collapse in={showFilters}>
        <Paper component="aside" id="filters-panel" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" component="h3" gutterBottom>Filter by:</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Content Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Account"
                value={filterConfig.account || ''}
                onChange={(e) => onFilterChange('account', e.target.value)}
                placeholder="Search by account name"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sector</InputLabel>
                <Select
                  value={filterConfig.sector || ''}
                  label="Sector"
                  onChange={(e) => onFilterChange('sector', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableSectors.map(sector => (
                    <MenuItem key={sector} value={sector}>{sector}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Platform</InputLabel>
                <Select
                  value={filterConfig.platform || ''}
                  label="Platform"
                  onChange={(e) => onFilterChange('platform', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availablePlatforms.map(platform => (
                    <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Topic"
                value={filterConfig.topic || ''}
                onChange={(e) => onFilterChange('topic', e.target.value)}
                placeholder="Search by topic"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Persona"
                value={filterConfig.icp || ''}
                onChange={(e) => onFilterChange('icp', e.target.value)}
                placeholder="Search by persona"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Created By"
                value={filterConfig.createdBy || ''}
                onChange={(e) => onFilterChange('createdBy', e.target.value)}
                placeholder="Search by name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Impact Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Projected Impact</InputLabel>
                <Select
                  value={filterConfig.projectedImpact || ''}
                  label="Projected Impact"
                  onChange={(e) => onFilterChange('projectedImpact', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableImpactLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Date Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Created After"
                type="date"
                size="small"
                fullWidth
                value={filterConfig.createdAfter || ''}
                onChange={(e) => onFilterChange('createdAfter', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Created Before"
                type="date"
                size="small"
                fullWidth
                value={filterConfig.createdBefore || ''}
                onChange={(e) => onFilterChange('createdBefore', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<ClearIcon />}
                onClick={onClearFilters}
                disabled={Object.keys(filterConfig).length === 0}
                variant="outlined"
                color="primary"
                fullWidth={isMobile}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </>
  );
};

export default IdeaFilters;